import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { App } from "antd";

import { useSurveyViewForm } from "@/views/survey-views/hooks/useSurveyViewForm";
import {
  useGetSurveyViewByIdQuery,
  useUpdateSurveyViewMutation,
} from "@/redux/service";
import SurveyViewForm from "@/views/survey-views/components/SurveyViewForm";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { SurveyViewFormValues } from "@/views/survey-views/types";
import { SurveyViewColorScheme } from "@/redux/survey-views/types";
import { SharingRights } from "@/utilities/constants";

const SurveyViewUpdate = () => {
  const { surveyViewUuid } = useParams();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { form } = useSurveyViewForm();
  const [updateSurveyView] = useUpdateSurveyViewMutation();
  const surveyViewResponse = useGetSurveyViewByIdQuery(
    surveyViewUuid as string,
  );
  const [originalEvidenceIds, setOriginalEvidenceIds] = useState<number[]>([]);

  useEffect(() => {
    if (surveyViewResponse.data) {
      const originalIds = surveyViewResponse.data.answers.reduce<number[]>(
        (acc, answer) => {
          answer.blocks.forEach((block) => {
            if (
              [
                SharingRights.PRME_COMMUNITY.value,
                SharingRights.PRIVATE.value,
              ].includes(block.sharingRights)
            ) {
              acc.push(block.id);
            }
          });
          return acc;
        },
        [],
      );

      setOriginalEvidenceIds(originalIds);

      form.reset({
        id: surveyViewUuid,
        title: surveyViewResponse.data.title,
        description: surveyViewResponse.data.description,
        survey: surveyViewResponse.data.survey,
        config: {
          ...surveyViewResponse.data.config,
          grayScale:
            surveyViewResponse.data.config.colorScheme ===
            SurveyViewColorScheme.GRAY_SCALE,
        },
        answers: surveyViewResponse.data.answers.map((answer) => ({
          answer: answer.answer,
          question: answer.question,
          blocks: answer.blocks.map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          })),
        })),
      });
    }
  }, [surveyViewResponse.data]);

  const handleSubmit = async (values: SurveyViewFormValues) => {
    if (!surveyViewUuid) return;

    const data = {
      version: 2,
      ...values,
      config: {
        ...values.config,
        colorScheme: values.config.grayScale
          ? SurveyViewColorScheme.GRAY_SCALE
          : SurveyViewColorScheme.DEFAULT,
      },
      answers: values.answers.map((answer) => ({
        ...answer,
        blocks: answer.blocks.map((block) => ({
          id: block.id,
        })),
      })),
    };
    const response = await updateSurveyView({
      id: surveyViewUuid,
      data,
    }).unwrap();
    notification.success({
      message: t("surveyViewUpdated"),
    });
    navigate(`/reports/views/${response.id}`);
    form.reset();
  };

  if (surveyViewResponse.isLoading) {
    return <BaseSpinner />;
  }

  return (
    <SurveyViewForm
      form={form}
      handleSubmit={form.handleSubmit(handleSubmit)}
      originalEvidenceIds={originalEvidenceIds}
    />
  );
};

export default SurveyViewUpdate;
